import { PropsWithChildren, RefObject, useLayoutEffect, useState } from 'react';
import {
  Button,
  Paper,
  Portal,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import UndoIcon from '@material-ui/icons/Undo';
import {
  SidebarOpenStateProvider,
  useSidebarOpenState,
} from '@backstage/core-components';
import { alpha } from '@mui/material/styles';
import { getSidebarFlyoutPalette } from './SidebarHoverFlyout';

const useStyles = makeStyles(theme => {
  const { color, surface } = getSidebarFlyoutPalette(theme);
  return {
    panel: {
      position: 'fixed',
      top: 12,
      bottom: 12,
      width: 304,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      color,
      background: surface,
      border: `1px solid ${theme.palette.divider}`,
      borderLeft: 0,
      borderRadius: '0 12px 12px 0',
      boxShadow: '6px 4px 24px rgba(0, 0, 0, 0.14)',
      zIndex: theme.zIndex.appBar + 1,
    },
    header: { padding: theme.spacing(2.5, 2, 2), flexShrink: 0 },
    title: { fontSize: 16, fontWeight: 700, lineHeight: 1.5 },
    hint: {
      marginTop: theme.spacing(0.75),
      fontSize: 13,
      lineHeight: 1.5,
      opacity: 0.7,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      flex: 1,
      overflowY: 'auto',
      margin: theme.spacing(0, 1, 1),
      borderRadius: 8,
    },
    removing: {
      background: alpha(theme.palette.primary.main, 0.06),
      outline: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
    },
    empty: {
      margin: theme.spacing(0.5),
      padding: theme.spacing(4, 2),
      border: `1px dashed ${alpha(color, 0.2)}`,
      borderRadius: 8,
      textAlign: 'center',
      '& > svg': { fontSize: 28, marginBottom: theme.spacing(1), opacity: 0.5 },
    },
    emptyTitle: { fontSize: 13, fontWeight: 600 },
    footer: {
      padding: theme.spacing(1.5, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
    },
    caption: { fontSize: 11, opacity: 0.6 },
    done: {
      textTransform: 'none',
      borderRadius: 6,
      fontWeight: 600,
      minWidth: 80,
    },
    accessibleInstructions: {
      position: 'absolute',
      width: 1,
      height: 1,
      overflow: 'hidden',
      clipPath: 'inset(50%)',
      whiteSpace: 'nowrap',
    },
  };
});

export const SidebarCustomizationPanel = ({
  children,
  anchorRef,
  panelRef,
  availableCount,
  removing,
  instructionsId,
  id,
  onDone,
}: PropsWithChildren<{
  anchorRef: RefObject<HTMLDivElement>;
  panelRef: RefObject<HTMLDivElement>;
  availableCount: number;
  removing: boolean;
  instructionsId: string;
  id: string;
  onDone: () => void;
}>) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const [left, setLeft] = useState(0);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return undefined;
    const update = () => setLeft(anchor.getBoundingClientRect().right);
    update();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(update);
    observer?.observe(anchor);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, isOpen]);

  useLayoutEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('button')?.focus();
  }, [panelRef]);

  return (
    <Portal>
      <Paper
        ref={panelRef}
        id={id}
        className={classes.panel}
        style={{ left, maxWidth: `calc(100vw - ${left}px - 12px)` }}
        role="region"
        aria-label="Sidebar customization"
      >
        <SidebarOpenStateProvider value={{ isOpen: true, setOpen: () => {} }}>
          <div className={classes.header}>
            <Typography component="h2" className={classes.title}>
              Customize sidebar
            </Typography>
            <Typography className={classes.hint}>
              Drag items in to pin. Drag them out to remove.
            </Typography>
            <span
              id={instructionsId}
              className={classes.accessibleInstructions}
            >
              Use Enter to pin, Delete to remove, and Alt + arrow keys to
              reorder. Press Escape to finish.
            </span>
          </div>
          <div
            className={`${classes.content} ${removing ? classes.removing : ''}`}
          >
            {availableCount === 0 ? (
              <div className={classes.empty}>
                {removing ? <UndoIcon /> : <CheckIcon />}
                <Typography className={classes.emptyTitle}>
                  All items are pinned
                </Typography>
                <Typography className={classes.hint}>
                  Drop an item here to return it to More.
                </Typography>
              </div>
            ) : (
              children
            )}
          </div>
          <div className={classes.footer}>
            <div className={classes.actions}>
              <Typography className={classes.caption}>
                Changes apply instantly
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                className={classes.done}
                startIcon={<CheckIcon />}
                onClick={onDone}
              >
                Done
              </Button>
            </div>
          </div>
        </SidebarOpenStateProvider>
      </Paper>
    </Portal>
  );
};
